import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {userRolesOptions} from '../../constants/userRolesOptions';
import {fadeInOut} from '../../../shared/animations/animations';
import {SendInvitation} from '../../../shared/states/profile/profile.actions';
import {take, withLatestFrom} from 'rxjs/operators';
import {Select, Store} from '@ngxs/store';
import {ProfileState} from '../../../shared/states/profile/profile.state';
import {Observable} from 'rxjs/internal/Observable';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DropdownComponent} from '../../../shared/components/dropdown/dropdown.component';

@UntilDestroy()
@Component({
  selector: 'app-invite-teammates-dialog',
  templateUrl: './invite-teammates-dialog.component.html',
  styleUrls: ['./invite-teammates-dialog.component.scss'],
  animations: [fadeInOut],
})
export class InviteTeammatesDialogComponent implements OnInit, AfterViewInit {
  @Input() show: boolean;

  @Output() afterSave: EventEmitter<void> = new EventEmitter();
  @Output() closeDialog: EventEmitter<void> = new EventEmitter();

  inviteUserForm: UntypedFormGroup;

  userRolesOptions = userRolesOptions;

  userItems: UntypedFormArray;
  failedEmailsFlags = [];

  @ViewChild('addMoreLabel') addMoreLabel;
  @ViewChild('closeButton') closeButton;
  @ViewChild('submitButton') submitButton;
  @ViewChildren('emailInputList') emailInputList: QueryList<any>;
  @ViewChildren('roleDropdownList') roleDropdownList: QueryList<DropdownComponent>;

  @Select(ProfileState.success) success$: Observable<boolean>;
  @Select(ProfileState.failedInvitationEmails) failedInvitationEmails$: Observable<string[]>;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    this.inviteUserForm = fb.group({
      users: fb.array([this.createUserItem()]),
    });
  }

  ngOnInit(): void {
    this.userItems = this.inviteUserForm.get('users') as UntypedFormArray;
    this.inviteUserForm.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.failedEmailsFlags.forEach(el => el = false);
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusInput(0));
  }

  openDropdown(i: number): void {
    const dropdownArray = this.roleDropdownList.toArray();

    if (dropdownArray && dropdownArray[i]) {
      setTimeout(() => dropdownArray[i].inputClick());
    }
  }

  addInviteFormItem(): void {
    if (this.inviteUserForm.get('users').valid) {
      this.userItems.push(this.createUserItem());
    }
  }

  removeInviteFormItem(i): void {
    this.failedEmailsFlags.splice(i, 1);
    this.userItems.removeAt(i);
  }

  createUserItem(): UntypedFormGroup {
    this.failedEmailsFlags.push(false);
    return this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      role: ['', [Validators.required]],
    });
  }

  close(): void {
    this.failedEmailsFlags = [];
    this.userItems.clear();
    this.userItems.push(this.createUserItem());
    this.closeDialog.emit();
  }

  invite(): void {
    this.failedEmailsFlags.forEach((el, index) => {
      this.failedEmailsFlags[index] = false;
    });

    this.store.dispatch(new SendInvitation(this.inviteUserForm.value?.users))
      .pipe(take(1), withLatestFrom(this.success$, this.failedInvitationEmails$))
      .subscribe(([_, success, failedInvitationEmails]) => {
        if (failedInvitationEmails && failedInvitationEmails.length) {
          this.failedEmailsFlags.forEach((el, index) => {
            if (failedInvitationEmails.includes(this.inviteUserForm.value?.users[index]?.email)) {
              this.failedEmailsFlags[index] = true;
            }
          });
        } else if (success) {
          this.afterSave.emit();
          this.close();
        }
      });
  }

  isSomeEmailDuplicated(): boolean {
    return (new Set(this.inviteUserForm.get('users')?.value.map(el => el.email))).size !==
      this.inviteUserForm.get('users')?.value.length;
  }

  isEmailDuplicated(email: string, index: number): boolean {
    return !!this.inviteUserForm.get('users')?.value.find((el, i) => index !== i && el.email === email);
  }

  focusButton(buttonName: string): void {
    setTimeout(() => this[buttonName]?.focus());
  }

  focusInput(i: number): void {
    const inputsArray = this.emailInputList.toArray();

    if (inputsArray && inputsArray[i]) {
      setTimeout(() => inputsArray[i].appInput?.nativeElement?.focus());
    }
  }

  focusLabel(): void {
    setTimeout(() => this.addMoreLabel?.nativeElement?.focus());
  }

  afterDropdownClosed(i: number): void {
    // tslint:disable-next-line
    if (i === this.inviteUserForm.get('users')['controls']?.length - 1) {
      this.inviteUserForm.get('users').valid ? this.focusLabel() : this.focusButton('closeButton');
    } else {
      this.focusInput(i + 1);
    }
  }
}
